// Third-party
import Box from '@mui/material/Box';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import { Fragment, useEffect, useState } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import SvgIcon from '@mui/material/SvgIcon';
import LoadingButton from '@mui/lab/LoadingButton';

// Components
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import Card from '@mui/material/Card';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { BaseCheckbox } from '@mgm/common/components/base';
import { SubmissionBlockWrapper } from '../../Shared';
import { NoteDialog } from './NoteDialog';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { useSubmitTasklistAPI } from '../../Shared/hooks/api';
import { useSharedTasklists } from '../../Shared/hooks';

// Redux-toolkit
import { getSelectedTasklistToSubmit } from '../../../../store/tasklists/selectors';
import { tasklistsActions } from '../../../../store/tasklists/slice';

// Assets
import { ReactComponent as NoteIcon } from '../../Shared/static/ic_note.svg';

// Types
import { TTasklistTask } from '../../../../types';
import { TSubmitTasklistReq } from '../../../../types/requests';

const ToSubmitBlock = () => {
  const { t } = useTranslation('tasklists');
  const { mutateAsync: submitTasklistMutate, isLoading } = useSubmitTasklistAPI();
  const selectedTasklistToSubmit = useAppSelector(getSelectedTasklistToSubmit);
  const { tasklistsToSubmit } = useSharedTasklists();
  const dispatch = useAppDispatch();
  const theme = useTheme() as TCustomTheme;

  const { isMobile } = useCustomTheme(theme);

  const [tasks, setTasks] = useState<Array<TTasklistTask>>([]);
  const [isOpenNoteDialog, setIsOpenDialog] = useState(false);
  const [submissionNote, setSubmissionNote] = useState('');

  useEffect(() => {
    setSubmissionNote('');
    setTasks([...(selectedTasklistToSubmit?.tasks || [])]);
  }, [selectedTasklistToSubmit]);

  const changeTask = (index: number) => {
    const updatedTasks = [...tasks];

    updatedTasks[index] = {
      ...updatedTasks[index],
      isDone: !updatedTasks[index].isDone,
    };

    setTasks(updatedTasks);
  };

  const openNoteDialog = () => {
    setIsOpenDialog(true);
  };

  const handleSubmit = async () => {
    const formattedTasks = tasks.map((task) => ({
      isDone: task.isDone || false,
      name: task.taskName,
    }));

    const data = {
      tasklistId: selectedTasklistToSubmit.id,
      note: submissionNote,
      tasklistName: selectedTasklistToSubmit.title,
      isAssigned: selectedTasklistToSubmit.isAssigned,
      tasks: formattedTasks,
    } as TSubmitTasklistReq;

    await submitTasklistMutate(data);

    dispatch(tasklistsActions.setSelectedTasklistToSubmit(tasklistsToSubmit[0]));
    dispatch(tasklistsActions.setSubmissionsTab('1'));
  };

  return (
    <SubmissionBlockWrapper selectedSubmission={selectedTasklistToSubmit}>
      <Card
        sx={{
          boxShadow: 'none',
        }}
        elevation={0}
      >
        <CardHeader
          sx={{
            p: 2,
            px: isMobile ? 1.5 : 3,
          }}
          title={selectedTasklistToSubmit.title}
          action={(
            <Stack marginTop={1} direction="row" spacing={2}>
              <SvgIcon
                className="cursor-pointer"
                onClick={openNoteDialog}
                fontSize="large"
                component={NoteIcon}
                inheritViewBox
              />
              <LoadingButton
                loading={isLoading}
                onClick={handleSubmit}
                size="small"
                variant="contained"
              >
                {t('submit')}
              </LoadingButton>
            </Stack>
           )}
        />
        <Box sx={{ px: isMobile ? 1.5 : 3, py: 1 }}>
          {tasks?.map((task, index) => (
            <Fragment key={task.taskName}>
              <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
                <FormControlLabel
                  control={(
                    <BaseCheckbox
                      handleClick={() => changeTask(index)}
                      value={task}
                      checked={task.isDone || false}
                    />
                  )}
                  label={(
                    <Typography
                      variant="body2"
                      sx={{
                        ...(task.isDone && {
                          color: 'text.disabled',
                          textDecoration: 'line-through',
                        }),
                      }}
                    >
                      {task.taskName}
                    </Typography>
                  )}
                />
              </Stack>
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Fragment>
          ))}
        </Box>
      </Card>
      <NoteDialog
        isOpenNoteDialog={isOpenNoteDialog}
        handleClose={() => setIsOpenDialog(false)}
        submissionNote={submissionNote}
        setSubmissionNote={setSubmissionNote}
      />
    </SubmissionBlockWrapper>
  );
};

export {
  ToSubmitBlock,
};
